import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import { HeightSize } from '~/constants/form';
import { ResponsiveBreakPoints } from '~/constants/responsive';
import * as React from 'react';
import { WrappedFieldProps } from 'redux-form';
import { colors, formSizes } from '~/styles/constants';

type StyledInputTheme = {
  backgroundColor: string;
  borderColor: string;
  color: string;
  isRightAligned: boolean;
};

interface StyledInputProps {
  isResponsive?: boolean;
  hasPrefix?: boolean;
  hasSuffix?: boolean;
}

const StyledInputContainer = styled('div')<StyledInputProps & { theme?: StyledInputTheme }>`
  border-radius: 3px;
  background-color: ${(props) => props.theme.backgroundColor};
  border: 1px solid ${(props) => props.theme.borderColor};
  color: ${(props) => props.theme.color};

  ${(props) => (!props.hasPrefix ? 'padding-left: 10px' : '')};
  ${(props) => (!props.hasSuffix ? 'padding-right: 10px' : '')};
  /* Input is 35px.. subtracting by 2 because 2 extra pixels are added by the borders 1px */
  height: ${HeightSize.medium - 2}px;
  width: 100%;
  @media screen and (min-width: ${ResponsiveBreakPoints.Mobile}px) {
    /* width: ${formSizes.inputWidth}; */
    width: 74px;
  }
  display: flex;
  align-items: center;
`;

const StyledInput = styled('input')<{ theme?: StyledInputTheme }>`
  font-size: 14px;
  height: 18px;
  line-height: 18px;
  border: none;
  width: 100%;
  outline: none;
  overflow: hidden;
  ${(props) => (props.theme.isRightAligned ? 'text-align: right;' : '')};

  ::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: ${colors.form.placeholderFontColor};
    opacity: 1; /* Firefox */
  }

  ::-ms-input-placeholder {
    /* Microsoft Edge */
    color: ${colors.form.placeholderFontColor};
  }

  :-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: ${colors.form.placeholderFontColor};
  }

  :focus,
  :active {
    border-color: inherit;
  }

  :disabled {
    background-color: inherit;
    color: inherit;
  }
`;

const Prefix = styled('div')`
  height: 20px;
  width: 20px;
  background-color: ${colors.gray25};
  border-radius: 2px;
  text-align: center;
  font-size: 13px;
  line-height: 20px;
  margin: 0 5px 0 6px;
`;

const Suffix = styled('div')`
  padding: 0 11px 0 4px;
  font-size: 13px;
`;

type inputTypes =
  | 'button'
  | 'checkbox'
  | 'color'
  | 'date'
  | 'datetime-local'
  | 'email'
  | 'file'
  | 'hidden'
  | 'image'
  | 'month'
  | 'number'
  | 'password'
  | 'radio'
  | 'range'
  | 'reset'
  | 'search'
  | 'submit'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week';

export interface InputFieldProps {
  // Required props
  id?: string;
  // Optional props
  placeholder?: string;
  disabled?: boolean;
  type?: inputTypes;
  className?: string;
  autoComplete?: 'on' | 'off';
  tabIndex?: number;
  isAutoFocus?: boolean;
  isAutoSelect?: boolean;
  isResponsive?: boolean;
  dataAnalytics?: string;
  dataPendo?: string;
  dataE2E?: string;
  inputRef?: string | React.RefObject<HTMLInputElement>;
  isInvalid?: boolean;
  isRightAligned?: boolean;
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search'
    | undefined;
  // Events
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  // Render props
  renderPrefix?: (value: string) => JSX.Element | string;
  renderSuffix?: (value: string) => JSX.Element | string;
}

export interface InputProps extends InputFieldProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface InputState {
  isFocused: boolean;
}

class InputExtended extends React.PureComponent<InputProps, InputState> {
  constructor(props: InputProps) {
    super(props);

    this.state = {
      isFocused: false,
    };
  }

  onInputFocus = () => {
    this.setState({ isFocused: true });
    this.props.onFocus && this.props.onFocus();
  };

  onInputBlur = () => {
    this.setState({ isFocused: false });
    this.props.onBlur && this.props.onBlur();
  };

  getTheme = (): StyledInputTheme => {
    const theme: StyledInputTheme = {
      backgroundColor: 'inherit',
      borderColor: colors.form.greyBorder,
      color: colors.form.fontColor,
      isRightAligned: !!this.props.isRightAligned,
    };

    if (this.props.isInvalid) {
      theme.borderColor = colors.dangerRed;
    }

    if (this.state.isFocused) {
      theme.borderColor = colors.primaryAlternative;
    }

    if (this.props.disabled) {
      theme.backgroundColor = colors.form.greyDisabled;
      theme.borderColor = colors.form.greyDisabled;
      theme.color = colors.form.disabledLabelFontColor;
    }

    return theme;
  };

  render() {
    const props = this.props;
    const {
      id,
      inputRef,
      value,
      onChange,
      placeholder,
      disabled,
      type,
      inputMode,
      autoComplete,
      tabIndex,
      isAutoFocus,
      onKeyDown,
      renderPrefix,
      renderSuffix,
      dataPendo,
      dataE2E,
      dataAnalytics,
    } = props;

    const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
      id,
      value,
      onChange,
      placeholder,
      tabIndex,
      onKeyDown,
      autoComplete,
      disabled,
      type: type || 'text',
      inputMode,
      onFocus: this.onInputFocus,
      onBlur: this.onInputBlur,
      autoFocus: isAutoFocus,
    };

    return (
      <ThemeProvider theme={this.getTheme()}>
        <StyledInputContainer
          className={props.className}
          isResponsive={props.isResponsive}
          hasPrefix={!!renderPrefix}
          hasSuffix={!!renderSuffix}
        >
          {renderPrefix ? renderPrefix(value) : null}
          <StyledInput
            {...inputProps}
            ref={inputRef}
            data-pendo={dataPendo}
            data-e2e={dataE2E}
            data-analytics={dataAnalytics}
          />
          {renderSuffix ? renderSuffix(value) : null}
        </StyledInputContainer>
      </ThemeProvider>
    );
  }
}

export const InputExtendedField = (props: WrappedFieldProps & InputFieldProps) => {
  const { input, meta, ...inputProps } = props;
  const invalid = meta && meta.touched && meta.error;
  const errors = invalid ? meta.error : null;

  return (
    <InputExtended
      value={input.value}
      onChange={input.onChange}
      isInvalid={invalid}
      {...inputProps}
    >
      {errors}
    </InputExtended>
  );
};

export default InputExtended;

export { Prefix, Suffix };
